import MenuSelector from '../../core/MenuSelector';
import PagingFilter from '../../core/PagingFilter';
import ProjectLanguageSelector from '../../core/ProjectLanguageSelector';
import { v4 as uuidv4 } from 'uuid';
import { Typography } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { withStyles } from '@material-ui/core/styles';
import AssignmentTurnedInIcon from '@material-ui/icons/AssignmentTurnedIn';
import EmojiObjectsIcon from '@material-ui/icons/EmojiObjects';
import AssignmentIcon from '@material-ui/icons/Assignment';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import InAppBar from '../../core/InAppBar';
import PrimaryButton from '../../core/PrimaryButton';
import ProjectUsers from '../../core/ProjectUsers';
import GS from '../../grafsync/grafsync';
import ActionText from '../ActionText';
import Errored from '../Errored';
import Loading from '../Loading';
import Paging from '../Paging';
import { globalStyles } from '../style/gstheme';
import { STATUS_ACCEPTED, STATUS_DRAFT, STATUS_PENDING, STATUS_REJECTED } from '../utils/constants';
import { ENTITLEMENTS } from '../../application-core/constants';
import { makeCancelable, sortObjects, sortByProp, computeLabelFromType } from '../utils/utils';
import ResourceDisplay from './ResourceDisplay';
import TaskDisplay from './TaskDisplay';
import SuggestionDisplay from './SuggestionDisplay';
import ComponentSelector from '../../application-core/ComponentSelector';
import { SUGGESTION_TYPE_OPTIONS } from '../../vocabulary/constants';

const APPROVED = STATUS_ACCEPTED;
const DRAFT = STATUS_DRAFT;
const PENDING = STATUS_PENDING;
const REJECTED = STATUS_REJECTED;
const SUGGESTIONS = 2;
const TASKS = 1;
const WORKFLOW = 0;

const PROJECT_TYPES = {
  "DataProject": [
    "https://grafsync.graphifi.com/schema/gs/Resource"
  ],
  "OntologyProject": [
    "http://www.w3.org/2002/07/owl#Class",
    "http://www.w3.org/2002/07/owl#ObjectProperty",
    "http://www.w3.org/2002/07/owl#DatatypeProperty",
    "http://www.w3.org/2002/07/owl#AnnotationProperty",
    "http://www.w3.org/2002/07/owl#NamedIndividual",
    "http://www.w3.org/2000/01/rdf-schema#Datatype"
  ],
  "TaxonomyProject": [
    "http://www.w3.org/2004/02/skos/core#Concept",
    "http://www.w3.org/2008/05/skos-xl#Label"
  ]
};

const SUGGESTION_TYPES = {
  "TaxonomyProject": [
    "https://grafsync.graphifi.com/schema/vm/DeprecationSuggestion",
    "https://grafsync.graphifi.com/schema/vm/RemovalSuggestion",
    "https://grafsync.graphifi.com/schema/vm/MovingSuggestion",
    "https://grafsync.graphifi.com/schema/vm/TerminologySuggestion",
    "https://grafsync.graphifi.com/schema/vm/MergeSuggestion",
    "https://grafsync.graphifi.com/schema/vm/SplitSuggestion",
    "https://grafsync.graphifi.com/schema/vm/SpellingSuggestion",
    "https://grafsync.graphifi.com/schema/vm/SynonymSuggestion",
    "https://grafsync.graphifi.com/schema/vm/LabelSuggestion",
    "https://grafsync.graphifi.com/schema/vm/NewConceptSuggestion"
  ]
};

const styles = () => {
  return Object.assign({
    taskResults: {
      marginTop: 12,
      padding: 12,
      borderRadius: 4,
      backgroundColor: '#f5f5f5'
    },
    tasks: {
      marginRight: 30,
      marginBottom: 30,
      backgroundColor: '#ffffff',
      borderRadius: 4,
      overflow: 'auto',
      padding: 12
    },
    taskSection: {
      padding: 12,
      borderRadius: 4,
      backgroundColor: 'white'
    }
  }, globalStyles);
};

class TaskManager extends Component {

  constructor(props) {
    super(props);
    const projectType = props.projectType;
    const usable = ["Managed", "Trial", "Premium"].includes(GS.getActiveSubscriptionType());
    const labelProp = projectType === "DataProject" || projectType === "OntologyProject" ?
      "http://www.w3.org/2000/01/rdf-schema#label"
      :
      "http://www.w3.org/2004/02/skos/core#prefLabel";
    const typeOptions = PROJECT_TYPES[projectType]
      .filter(pt => props.data.skosXL || pt !== "http://www.w3.org/2008/05/skos-xl#Label")
      .map(pt => ({
        value: pt,
        label: computeLabelFromType(GS.getAlias(pt))
      }));
    this.state = {
      isIn: projectType === "DataProject" ?
        "https://grafsync.graphifi.com/schema/gs/inDataset"
        :
        projectType === "OntologyProject" ?
          "https://grafsync.graphifi.com/schema/gs/visibility"
          :
          "http://www.w3.org/2004/02/skos/core#inScheme",
      labelProp,
      typeOptions,
      labelAlias: GS.getAlias(labelProp),
      tabValue: usable ? WORKFLOW : SUGGESTIONS,
      workflowInfoCount: 0,
      tasksInfoCount: 0,
      suggestionsInfoCount: 0,
      page: 1,
      text: "",
      showType: props.showType,
      suggestionType: "All",
      status: STATUS_PENDING,
      refresh: false,
      searchID: uuidv4(),
      initialLoading: true,
      componentIsActivated: undefined,
      lang: this.props.data.defaultLang,
      pageCount: undefined,
      searchCount: undefined,
      pageSize: 20,
      taskStatusCounts: undefined,
      user: usable ? props.user.id : "UNASSIGNED",
      isErrored: undefined,
      isLoading: true,
      resources: undefined,
      component: "All",
      usable
    };
    this.cancellable = null;
  }

  componentDidMount() {
    this.getData(this.state.searchID, true);
  }

  handleComponentChange = async (component) => {
    try {
      let isWorkflowActivated = undefined;
      if (component !== "All") {
        await this.loadData([component], {
          "predicates": [
            "http://purl.org/dc/terms/title",
            "https://grafsync.graphifi.com/schema/gs/activateWorkflow"
          ]
        }).promise;
        const componentData = GS.data(component, this.props.graph, false);
        isWorkflowActivated = componentData ?
          componentData.activateWorkflow === true
          :
          false;
      }
      this.setState({
        component,
        componentIsActivated: isWorkflowActivated,
        workflowInfoCount: 0,
        tasksInfoCount: 0,
        suggestionsInfoCount: 0
      }, () => {
        if (this.state.tabValue === SUGGESTIONS || component === "All" || isWorkflowActivated) {
          const searchID = uuidv4();
          this.setState({
            searchID,
            page: 1,
            refresh: false,
            taskStatusCounts: undefined,
            pageCount: undefined,
            searchCount: undefined,
            resources: undefined
          }, () => this.getData(searchID, true));
        }
        else {
          this.setState({
            page: 1,
            refresh: false,
            taskStatusCounts: undefined,
            pageCount: undefined,
            searchCount: undefined,
            resources: undefined
          });
        }
      });
    }
    catch (e) {
      if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
    }
  };

  handleUserChange = (user) => {
    const searchID = uuidv4();
    this.setState({
      page: 1,
      refresh: false,
      taskStatusCounts: undefined,
      pageCount: undefined,
      searchCount: undefined,
      resources: undefined,
      user,
      searchID
    }, () => this.getData(searchID, true));
  };

  getData = (searchID, computeCounts = false) => {
    try {
      this.setState({ isLoading: true, refresh: false }, async () => {
        try {
          const tabValue = this.state.tabValue;
          const entitlements = this.props.entitlements;
          let dataWorkflow = {};
          let dataTasks = {};
          let dataSuggestions = {};
          if (computeCounts) {
            dataWorkflow = await this.loadTasks(this.state.page, searchID + "-workflow", WORKFLOW);
            if (tabValue !== TASKS && (entitlements.includes(ENTITLEMENTS.VIEW_TASK) || entitlements.includes(ENTITLEMENTS.EDIT_TASK) || entitlements.includes(ENTITLEMENTS.CREATE_TASK))) {
              dataTasks = await this.loadTasks(this.state.page, searchID + "-tasks", TASKS);
            }
            if (tabValue !== SUGGESTIONS && this.props.enableSuggestions && (entitlements.includes(ENTITLEMENTS.VIEW_SUGGESTIONS) || entitlements.includes(ENTITLEMENTS.MAKE_SUGGESTIONS) || entitlements.includes(ENTITLEMENTS.MANAGE_SUGGESTIONS))) {
              dataSuggestions = await this.loadTasks(this.state.page, searchID + "-suggestions", SUGGESTIONS);
            }
          }

          GS.handle("get-data");
          const data = await this.loadTasks(this.state.page, searchID);
          if (this.cancellable && this.cancellable.isCancelled()) return;
          if (data.uuid && data.uuid !== this.state.searchID) return;
          // To make life easy for sorting if there are literalForm values we create labels in the default lang
          data.results.forEach(result => {
            if (result.literalForm) {
              result[this.state.labelAlias] = {};
              if (Object.keys(result.literalForm) && Object.keys(result.literalForm).length > 0) {
                result[this.state.labelAlias][this.props.data.defaultLang] = result.literalForm[Object.keys(result.literalForm)[0]];
              }
            }
          });
          const sortedResources = tabValue === WORKFLOW ?
            sortObjects(data.results, this.state.labelAlias, this.state.lang, null, false)
            :
            sortByProp(data.results, "created");
          const resourceIDs = sortedResources.map(result => result.id);
          const suggestionsIDs = sortedResources.map(result => result.id);
          const allResourceIDs = [...resourceIDs, ...suggestionsIDs];
          // May be multiple suggestions for same resource
          const uniqueAllResourcesIDs = [...new Set(allResourceIDs)];
          let taskStatusCounts = undefined;
          this.loadData(uniqueAllResourcesIDs, {})
            .promise
            .then(async () => {
              if (tabValue === WORKFLOW && data.results.length > 0) {
                taskStatusCounts = await this.loadTaskCounts(sortedResources);
                if (this.cancellable && this.cancellable.isCancelled()) return;
              }
              if (data.uuid && data.uuid !== this.state.searchID) return;
              const infoCount = "searchCount" in data ? data.searchCount : 0;
              this.setState({
                resources: sortedResources,
                taskStatusCounts,
                pageCount: data.pageCount,
                searchCount: data.searchCount,
                ...(computeCounts) &&
                {
                  workflowInfoCount: "searchCount" in dataWorkflow ? dataWorkflow.searchCount : 0,
                  tasksInfoCount: tabValue === TASKS ?
                    infoCount
                    :
                    "searchCount" in dataTasks ? dataTasks.searchCount : 0,
                  suggestionsInfoCount: tabValue === SUGGESTIONS ?
                    infoCount
                    :
                    "searchCount" in dataSuggestions ? dataSuggestions.searchCount : 0
                },
                page: data.page,
                isLoading: false,
                initialLoading: false
              });
            })
            .catch((e) => {
              if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
            });
        }
        catch (e) {
          if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
        }
      });
    }
    catch (e) {
      if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
    }
  };

  doSearch(payload, searchID) {
    this.cancellable = makeCancelable(GS.search(payload, searchID));
    return this.cancellable;
  }

  loadData(iris, options) {
    this.cancellable = makeCancelable(GS.loadData(iris, options, this.props.graph, true));
    return this.cancellable;
  }

  loadTasks = async (page = 1, searchID, autoTabValue) => {
    const tabValue = autoTabValue !== undefined ? autoTabValue : this.state.tabValue;
    const projectType = this.props.projectType;
    const workflowTypes =
      autoTabValue !== undefined || this.state.showType === "All" ?
        PROJECT_TYPES[projectType]
        :
        this.state.showType;

    const typeValues = tabValue === WORKFLOW ?
      workflowTypes
      :
      tabValue === TASKS ?
        [
          "https://grafsync.graphifi.com/schema/gs/Task",
        ]
        :
        this.state.suggestionType === "All" || autoTabValue !== undefined ?
          SUGGESTION_TYPES[projectType]
          :
          ["https://grafsync.graphifi.com/schema/vm/" + this.state.suggestionType]
      ;
    let search = [];
    if (this.state.user === "UNASSIGNED") {
      search.push({
        predicate: "https://grafsync.graphifi.com/schema/gs/assignedUser",
        invert: true
      });
    }
    else if (this.state.user !== "All") {
      search.push({
        predicate: "https://grafsync.graphifi.com/schema/gs/assignedUser",
        value: this.state.user
      });
    }
    else {
      search.push({
        predicate: "https://grafsync.graphifi.com/schema/gs/assignedUser"
      });
    }
    search.push({
      predicate: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
      value: typeValues
    }
    );
    if (tabValue === WORKFLOW) {
      search.push({
        predicate: "http://www.w3.org/2002/07/owl#deprecated",
        invert: true,
        value: 'true',
        isText: true,
        isExactMatch: true,
        datatype: "http://www.w3.org/2001/XMLSchema#boolean"
      });
    }
    // The following two help avoid deleted and deprecated and rejected concept/label/resource resources
    else if (tabValue === TASKS) {
      search.push({
        predicate: "https://grafsync.graphifi.com/schema/gs/isTaskFor",
        join: [
          {
            predicate: "http://www.w3.org/2002/07/owl#deprecated",
            invert: true,
            value: 'true',
            isText: true,
            isExactMatch: true,
            datatype: "http://www.w3.org/2001/XMLSchema#boolean"
          },
          {
            predicate: "https://grafsync.graphifi.com/schema/gs/status",
            invert: true,
            value: REJECTED
          },
          {
            predicate: this.state.isIn,
            ...(this.state.component !== "All") && { value: this.state.component },
            join: [{
              predicate: "https://grafsync.graphifi.com/schema/gs/activateWorkflow",
              value: 'true',
              isText: true,
              isExactMatch: true,
              datatype: "http://www.w3.org/2001/XMLSchema#boolean"
            }]
          }
        ]
      });
    }
    else {
      search.push({
        predicate: "https://grafsync.graphifi.com/schema/vm/suggestionSourceConcept",
        join: [{
          predicate: "http://www.w3.org/2002/07/owl#deprecated",
          invert: true,
          value: 'true',
          isText: true,
          isExactMatch: true,
          datatype: "http://www.w3.org/2001/XMLSchema#boolean"
        }]
      });
    }
    let sort = [this.state.labelProp];
    if (tabValue === WORKFLOW) {
      if (autoTabValue !== undefined) {
        sort.push("http://www.w3.org/2008/05/skos-xl#literalForm");
      }
      else if (this.state.showType === "http://www.w3.org/2008/05/skos-xl#Label") {
        sort = ["http://www.w3.org/2008/05/skos-xl#literalForm"];
      }
    }
    else {
      sort = ["http://purl.org/dc/terms/created"];
    }
    if (tabValue === WORKFLOW) {
      search.push({
        predicate: sort,
        ...(this.state.text !== "" && autoTabValue === undefined) && {
          value: this.state.text,
          isExactMatch: false
        }
      });
    }
    if (this.state.user === "UNASSIGNED") {
      search.push({
        predicate: "https://grafsync.graphifi.com/schema/gs/status",
        invert: tabValue !== SUGGESTIONS,
        value: tabValue === SUGGESTIONS ?
          [this.state.status]
          :
          [APPROVED, REJECTED]
      });
    }
    else {
      search.push({
        predicate: "https://grafsync.graphifi.com/schema/gs/status",
        value: tabValue === SUGGESTIONS ?
          [this.state.status]
          :
          [DRAFT, PENDING]
      });
    }
    if (tabValue !== TASKS) {
      search.push({
        predicate: this.state.isIn,
        ...(this.state.component !== "All") && { value: this.state.component },
        ...(tabValue !== SUGGESTIONS) && {
          join: [{
            predicate: "https://grafsync.graphifi.com/schema/gs/activateWorkflow",
            value: 'true',
            isText: true,
            isExactMatch: true,
            datatype: "http://www.w3.org/2001/XMLSchema#boolean"
          }]
        }
      });
    }
    const data = await this.doSearch({
      search,
      predicates: [
        this.state.labelProp,
        this.state.isIn,
        "http://www.w3.org/2008/05/skos-xl#literalForm",
        "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
        "http://purl.org/dc/terms/created"
      ],
      ...(this.state.user !== "UNASSIGNED") && { retainOrder: true },
      page,
      pageSize: 20,
      allowMissingResults: true,
      ...((this.props.projectType !== "OntologyProject" && autoTabValue !== undefined) || (this.props.data.skosXL && this.state.showType === "http://www.w3.org/2004/02/skos/core#Concept")) && { entailment: ["propertyChain"] },
      ...(autoTabValue === undefined && tabValue === WORKFLOW) && { language: this.state.lang },
      graph: [this.props.graph],
      basicSort: true,
      sort,
      fast: autoTabValue !== undefined
    }, searchID).promise;
    return data;
  };

  loadTaskCounts = async (resources) => {
    const resourceList = resources.map(r => r.id);
    let taskList = [];
    resourceList.forEach(r => {
      const data = GS.data(r, this.props.graph, false);
      if (data && data.hasTask) taskList = taskList.concat(data.hasTask);
    });
    let taskCountData = {};
    if (taskList.length === 0) {
      return taskCountData;
    }
    let search = [
      {
        predicate: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
        value: "https://grafsync.graphifi.com/schema/gs/Task",
      }
    ];
    const data = await this.doSearch({
      search,
      from: taskList,
      allowMissingResults: true,
      predicates: [
        "https://grafsync.graphifi.com/schema/gs/isTaskFor",
        "https://grafsync.graphifi.com/schema/gs/status"
      ],
      pageSize: 200,
      graph: [this.props.graph],
      basicSort: true
    }).promise;
    resourceList.forEach(r => {
      const resourceTasks = data.results.filter(d => d.isTaskFor === r && d.status);
      const pending = resourceTasks.filter(t => t.status[0] === PENDING).length;
      const rejected = resourceTasks.filter(t => t.status[0] === REJECTED).length;
      const approved = resourceTasks.filter(t => t.status[0] === APPROVED).length;
      taskCountData[r] = {
        pending,
        rejected,
        approved
      };
    });
    return taskCountData;
  };

  componentWillUnmount() {
    if (this.cancellable) this.cancellable.cancel();
  }

  handlePageChange = (page) => {
    const searchID = uuidv4();
    this.setState({
      page,
      refresh: false,
      searchID
    }, () => this.getData(searchID));
  };

  changeTab = (tabValue) => {
    const searchID = uuidv4();
    // We need to force isLoading here to avoid rendering when tabs switch
    this.setState({
      tabValue,
      searchID,
      refresh: false,
      pageCount: undefined,
      searchCount: undefined,
      resources: undefined
    }, () => this.getData(searchID));
  };

  handleRefresh = () => this.setState({ refresh: true });

  doRefresh = () => {
    const searchID = uuidv4();
    this.setState({
      page: 1,
      searchID,
      refresh: false,
      pageCount: undefined,
      searchCount: undefined,
      resources: undefined
    }, () => this.getData(searchID, true));
  };

  handleShowTypeChange = (event) => {
    const searchID = uuidv4();
    this.setState({
      page: 1,
      searchID,
      refresh: false,
      pageCount: undefined,
      searchCount: undefined,
      resources: undefined,
      showType: event.target.value
    }, () => this.getData(searchID));
  };

  changeLanguage = (lang) => {
    const searchID = uuidv4();
    this.setState({
      page: 1,
      searchID,
      refresh: false,
      pageCount: undefined,
      searchCount: undefined,
      resources: undefined,
      lang
    }, () => this.getData(searchID));
  };

  handleTypeChange = (showType) => {
    const searchID = uuidv4();
    this.setState({
      page: 1,
      searchID,
      refresh: false,
      pageCount: undefined,
      searchCount: undefined,
      resources: undefined,
      showType
    }, () => this.getData(searchID));
  };

  handleFilterChange = (text) => {
    const searchID = uuidv4();
    this.setState({
      page: 1,
      searchID,
      refresh: false,
      pageCount: undefined,
      searchCount: undefined,
      resources: undefined,
      text
    }, () => this.getData(searchID));
  };

  handleSuggestionTypeChange = (suggestionType) => {
    const searchID = uuidv4();
    this.setState({
      page: 1,
      searchID,
      refresh: false,
      pageCount: undefined,
      searchCount: undefined,
      resources: undefined,
      suggestionType
    }, () => this.getData(searchID));
  };

  handleStatusChange = (status) => {
    const searchID = uuidv4();
    this.setState({
      page: 1,
      searchID,
      refresh: false,
      pageCount: undefined,
      searchCount: undefined,
      resources: undefined,
      status
    }, () => this.getData(searchID));
  };

  render() {

    const { classes, data, project, graph, projectUsers, user, components, entitlements, allowAllTypes,
      componentType, projectType, enableSuggestions } = this.props;
    const { usable, isLoading, isErrored, resources, component, taskStatusCounts, lang, workflowInfoCount, tasksInfoCount, suggestionsInfoCount,
      page, pageCount, pageSize, searchCount, tabValue, refresh, showType, text, typeOptions,
      componentIsActivated, initialLoading } = this.state;

    let tabItems = [
      {
        icon: AssignmentTurnedInIcon,
        name: "Workflow",
        grey: !usable,
        label: <span>Workflow (<span data-test="workflow-count">{workflowInfoCount}</span>)</span>
      }
    ];
    tabItems.push({
      icon: AssignmentIcon,
      grey: !usable || !(entitlements.includes(ENTITLEMENTS.VIEW_TASK) || entitlements.includes(ENTITLEMENTS.EDIT_TASK) || entitlements.includes(ENTITLEMENTS.CREATE_TASK)),
      name: "Tasks",
      label: <span>Tasks (<span data-test="task-count">{tasksInfoCount}</span>)</span>
    });
    if (enableSuggestions && (entitlements.includes(ENTITLEMENTS.VIEW_SUGGESTIONS) || entitlements.includes(ENTITLEMENTS.MAKE_SUGGESTIONS) || entitlements.includes(ENTITLEMENTS.MANAGE_SUGGESTIONS))) {
      tabItems.push({
        icon: EmojiObjectsIcon,
        name: "Suggestions",
        label: <span>Suggestions (<span data-test="suggestion-count">{suggestionsInfoCount}</span>)</span>
      });
    }

    if (isErrored) return <Errored dataTest="tm-error" data={isErrored} />;
    if (initialLoading) return <Loading offset="120px" />;

    const componentList = components ? components.map(t => t.id) : [];
    const isWorkflowActivated = components && component !== "All" ? componentIsActivated : undefined;
    const fromResult = (page - 1) * pageSize + 1;
    const toResult = page * pageSize > searchCount ? searchCount : page * pageSize;

    const headers = tabValue === WORKFLOW ?
      ["Resource", componentType, "Status", "Assigned To", "Instructions", "Tasks", "Action", "Reassign"]
      :
      tabValue === TASKS ?
        ["Resource", componentType, "Status", "Assigned To", "Instructions", "Reassign"]
        :
        ["Placed On", componentType, "Status", "Suggestion Type", "Suggested Label", "Assigned To", "Detail", "Reassign"];

    let resourceTypeList = allowAllTypes ?
      [
        {
          value: "All",
          label: "All"
        },
        ...typeOptions
      ]
      :
      typeOptions;

    const isLargeData = this.props.data.largeDataSettings ? true : false;

    return (
      <div style={{ paddingBottom: 30, minWidth: 800 }}>
        <div className={classes.tasks}>
          <div data-test="search-filtering" className={classes.filterArea}>
            <Grid container spacing={2} classes={
              {
                "spacing-xs-2": this.props.classes.fullGrid
              }

            }>
              <Grid item xs={4}>
                <Typography className={classes.mainHeading} style={{ paddingTop: 0 }}>Jobs Manager</Typography>
              </Grid>
              <Grid item xs={2}>
                <ComponentSelector
                  id={project}
                  title={componentType}
                  defaultLang={data.defaultLang}
                  displayWorkflow={true}
                  components={componentList}
                  disabled={isLoading}
                  graph={graph}
                  handleChange={this.handleComponentChange} />
              </Grid>
              {usable &&
                <Grid item xs={2}>
                  <ProjectUsers
                    disabled={isLoading}
                    selected={user.id}
                    label="Assigned To"
                    additionalOptions={usable && isLargeData ?
                      []
                      :
                      [
                        {
                          value: "UNASSIGNED",
                          label: "Unassigned"
                        }
                      ]}
                    projectUsers={projectUsers}
                    onUserChange={this.handleUserChange} />
                </Grid>
              }
            </Grid>
          </div>
          <div>
            <div className={classes.taskResults}>
              <InAppBar
                tabValue={tabValue}
                background={false}
                onChangeTab={this.changeTab}
                tabItems={tabItems} />
              <div className={classes.taskSection}>
                {tabValue !== TASKS
                  && !(tabValue === WORKFLOW && workflowInfoCount === 0) &&
                  <div data-test="search-filtering" className={classes.filterArea}>
                    <Grid container spacing={2} classes={
                      {
                        "spacing-xs-2": this.props.classes.fullGrid
                      }
                    }>
                      {tabValue === WORKFLOW &&
                        <React.Fragment>
                          <Grid item xs={12} sm={4} md={3}>
                            <div style={{ paddingTop: 3 }}>
                              <PagingFilter
                                autoFocus={true}
                                disabled={isLoading}
                                lang={lang}
                                handleFilterChange={this.handleFilterChange}
                                text={text}
                                minLength={2}
                                includeLabel={true}
                                label="Label"
                                maxLength={50} />
                            </div>
                          </Grid>
                          {typeOptions.length > 1 &&
                            <Grid item xs={12} sm={4} md={3}>
                              <MenuSelector
                                dataTest="type-selector"
                                label="Resource Type"
                                handleChange={this.handleTypeChange}
                                selected={showType}
                                options={resourceTypeList} />
                            </Grid>
                          }
                          <Grid item xs={12} sm={4} md={3}>
                            <ProjectLanguageSelector
                              key={lang}
                              selectedLang={lang}
                              includeLabel={true}
                              disabled={isLoading}
                              onSelect={this.changeLanguage} />
                          </Grid>
                        </React.Fragment>
                      }
                      {tabValue === SUGGESTIONS &&
                        <React.Fragment>
                          <Grid item xs={12} sm={4} md={3}>
                            <MenuSelector
                              dataTest="type-selector"
                              label="Type"
                              disabled={isLoading}
                              selected="All"
                              handleChange={this.handleSuggestionTypeChange}
                              hintWord="suggestion type"
                              options={[
                                {
                                  "value": "All",
                                  "label": "All"
                                },
                                ...SUGGESTION_TYPE_OPTIONS
                              ]} />
                          </Grid>
                          <Grid item xs={12} sm={4} md={3}>
                            <MenuSelector
                              dataTest="status-selector"
                              label="Status"
                              selected={STATUS_PENDING}
                              handleChange={this.handleStatusChange}
                              hintWord=" suggestion status"
                              options={[
                                {
                                  "value": STATUS_PENDING,
                                  "label": "Open"
                                },
                                {
                                  "value": STATUS_ACCEPTED,
                                  "label": "Accepted"
                                },
                                {
                                  "value": STATUS_REJECTED,
                                  "label": "Rejected"
                                }
                              ]} />
                          </Grid>
                        </React.Fragment>
                      }
                    </Grid>
                  </div>
                }
                {isLoading ?
                  <div style={{ height: 250 }}>
                    <Loading offset="120px" />
                  </div>
                  :
                  !resources || resources.length === 0 ?
                    isWorkflowActivated !== false || tabValue === SUGGESTIONS ?
                      <div style={{ height: 140 }}>
                        <ActionText offset="120px">
                          {page > 1 ?
                            <span>Something has changed. Try refreshing!</span>
                            :
                            <span>Nothing to do!</span>
                          }
                        </ActionText>
                        {page > 1 &&
                          <div style={{ textAlign: 'center', marginTop: 20 }}>
                            <PrimaryButton dataTest="refresh" onClick={this.doRefresh}>Refresh</PrimaryButton>
                          </div>
                        }
                      </div>
                      :
                      <div style={{ height: 140 }}>
                        <ActionText offset="120px">Workflow is not activated for this component.</ActionText>
                      </div>
                    :
                    <div style={{ marginTop: 12, overflowX: 'scroll' }}>
                      <div>
                        {searchCount &&
                          <Typography
                            role="status"
                            data-test="showing-results"
                            variant="body2"
                            style={{ marginLeft: 6 }}
                            className={classes.showing}>Showing {fromResult} to {toResult} of {searchCount}</Typography>
                        }
                      </div>
                      <TableContainer style={{
                        marginTop: 6,
                        overflowX: "initial"
                      }}>
                        <Table data-test="jobs-viewer">
                          {resources.length > 0 &&
                            <React.Fragment>
                              <TableHead data-test="jobs-header">
                                <TableRow>
                                  {
                                    headers.map((col, index) => {
                                      return <TableCell
                                        data-test={"header-" + index}
                                        key={index}
                                        component="th"
                                        align={(index === 5 && [TASKS].includes(tabValue)) || index === 7 ?
                                          'right'
                                          :
                                          (index === 5 || index === 6) && tabValue === WORKFLOW ?
                                            'center'
                                            :
                                            'left'}
                                        style={{ ...(tabValue === WORKFLOW && index === 5) && { width: 136 } }}
                                        className={classes.tableHeading}>
                                        <span>
                                          {col}
                                        </span>
                                      </TableCell>;
                                    })}
                                </TableRow>
                              </TableHead>
                              <TableBody data-test="jobs-viewer-list">
                                {resources.map(resource => {
                                  return tabValue === WORKFLOW ?
                                    <ResourceDisplay
                                      id={resource.id}
                                      key={resource.id}
                                      viewLang={lang}
                                      projectType={projectType}
                                      components={this.props.components}
                                      defaultLang={data.defaultLang}
                                      project={project}
                                      user={user.id}
                                      onChange={this.handleRefresh}
                                      assignedFilter={this.state.user}
                                      taskStatusCounts={taskStatusCounts ? taskStatusCounts[resource.id] : undefined}
                                      entitlements={entitlements}
                                      suppressLoading={true}
                                      projectUsers={projectUsers}
                                      graph={graph} />
                                    :
                                    tabValue === TASKS ?
                                      <TaskDisplay
                                        suppressLoading={true}
                                        user={user.id}
                                        project={project}
                                        projectType={projectType}
                                        components={this.props.components}
                                        projectUsers={projectUsers}
                                        entitlements={entitlements}
                                        viewLang={data.defaultLang}
                                        onChange={this.handleRefresh}
                                        defaultLang={data.defaultLang}
                                        id={resource.id}
                                        key={resource.id}
                                        graph={graph}
                                      />
                                      :
                                      <SuggestionDisplay
                                        id={resource.id}
                                        key={resource.id}
                                        allowAssignment={usable}
                                        taxonomies={this.props.components}
                                        viewLang={data.defaultLang}
                                        defaultLang={data.defaultLang}
                                        project={project}
                                        assignedFilter={this.state.user}
                                        entitlements={entitlements}
                                        user={user.id}
                                        suppressLoading={true}
                                        onChange={this.handleRefresh}
                                        projectUsers={projectUsers}
                                        setToApproved={this.setToApproved}
                                        graph={graph} />;
                                })}
                              </TableBody>
                            </React.Fragment>
                          }
                        </Table>
                      </TableContainer>
                      {refresh &&
                        <div style={{ textAlign: 'center', marginTop: 20 }}>
                          <PrimaryButton dataTest="refresh" onClick={this.doRefresh}>Refresh</PrimaryButton>
                        </div>
                      }
                      {!refresh && resources && resources.length > 0 && pageCount > 1 &&
                        <div style={{ marginTop: '20px' }}>
                          <Paging page={page} pageCount={pageCount} pageItemsRange={5} handlePageChange={this.handlePageChange} />
                        </div>
                      }
                    </div>
                }
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

TaskManager.defaultProps = {
  enableSuggestions: false,
  showType: "All",
  allowAllTypes: false
};

TaskManager.propTypes = {
  classes: PropTypes.object,
  enableSuggestions: PropTypes.bool,
  showType: PropTypes.string,
  data: PropTypes.object.isRequired,
  componentType: PropTypes.string.isRequired,
  project: PropTypes.string.isRequired,
  projectUsers: PropTypes.array.isRequired,
  projectType: PropTypes.string.isRequired,
  entitlements: PropTypes.array.isRequired,
  components: PropTypes.array,
  user: PropTypes.object.isRequired,
  graph: PropTypes.string.isRequired
};

export default withStyles(styles)(TaskManager);